<template>
  <div class='container'>
    <h1 class="title">CALIMA Star Reinflector</h1>

    <div id='analyzer-input'>
      <div class='notification is-primary'>
        <p>
          <strong>CALIMA Star</strong> is an Arabic morphological analyzer and generator.
          Given a seed word and specifific inflectional features, the reinflector mode generates new words and their corresponding analyses.
          This demo is based on the work described in Taji et al. (2018). <sup>[1]</sup>
        </p>
      </div>
      <form v-on:keyup.enter.prevent="reinflect">
        <div class="columns">
          <!-- Word -->
          <div class="column is-one-fifth">
            <label class='label'>Word</label>
            <input dir='rtl' lang='ar' class='input ar-input' type='text' placeholder='' v-model='word' v-on:keydown.enter.prevent="">
          </div>

          <!-- Lemma -->
          <div class="column is-one-fifth">
            <label class='label'>{{ translations.feats['lex'][lang] }}</label>
            <input dir='rtl' lang='ar' class='input ar-input' type='text' placeholder='' v-model='lemma' v-on:keydown.enter.prevent="">
          </div>

          <!-- POS -->
          <div class="column is-one-fifth">
            <label class='label'>{{ translations.feats['pos'][lang] }}</label>
            <div class='select is-fullwidth'>
              <select v-model="pos">
                <!-- <option value='*'>{{ translations.pos['*'][lang] }}</option> -->
                <option v-bind:value='pos' v-for="pos in posList" v-if="pos != '*'" v-bind:key="pos">{{pos}} &emsp; ({{ translations.pos[pos][lang] }})</option>
              </select>
            </div>
          </div>
        </div>

        <!-- Features - Clitics -->
        <div class="columns">
          <div class="column is-one-fifth" v-for="feat in cliticFeats" v-bind:key="feat">
            <label class='label'>{{ translations.feats[feat][lang] }}</label>
            <div class='select is-fullwidth'>
              <select v-if="feat4pos[pos] && feat4pos[pos][feat]" v-model="feats[feat].value">
                <option v-bind:value='val' v-for="val in lodash.sortBy(feat4pos[pos][feat].options)" v-bind:key="val" v-if="feat4pos[pos] && feat4pos[pos][feat]">{{ translations.feats[feat].values[val][lang] }}</option>
              </select>
              <select disabled v-else></select>
            </div>
          </div>
        </div>

        <!-- Features - Other 1 -->
        <div class="columns">
          <div class="column is-one-fifth" v-for="feat in otherFeats1" v-bind:key="feat">
            <label class='label'>{{ translations.feats[feat][lang] }}</label>
            <div class='select is-fullwidth'>
              <select v-if="feat4pos[pos] && feat4pos[pos][feat]" v-model="feats[feat].value">
                <option v-if="!feat4pos[pos][feat].required" value="*">Default</option>
                <option v-if="!feat4pos[pos][feat].required" value="ANY">Any</option>
                <option v-bind:value='val' v-for="val in feat4pos[pos][feat].options" v-bind:key="val" v-if="feat4pos[pos] && feat4pos[pos][feat]">{{ translations.feats[feat].values[val][lang] }}</option>
              </select>
              <select disabled v-else></select>
            </div>
          </div>
        </div>

        <!-- Features - Other 2 -->
        <div class="columns">
          <div class="column is-one-fifth" v-for="feat in otherFeats2" v-bind:key="feat">
            <label class='label'>{{ translations.feats[feat][lang] }}</label>
            <div class='select is-fullwidth'>
              <select v-if="feat4pos[pos] && feat4pos[pos][feat]" v-model="feats[feat].value">
                <option v-if="!feat4pos[pos][feat].required" value="*">Default</option>
                <option v-if="!feat4pos[pos][feat].required" value="ANY">Any</option>
                <option v-bind:value='val' v-for="val in feat4pos[pos][feat].options" v-bind:key="val" v-if="feat4pos[pos] && feat4pos[pos][feat]">{{ translations.feats[feat].values[val][lang] }}</option>
              </select>
              <select disabled v-else></select>
            </div>
          </div>
        </div>

        <!-- Submit Button -->
        <div class="columns">
          <div class="column is-one-fifth is-flex">
            <a class="button is-primary is-fullwidth align-bottom" v-on:click="reinflect">Reinflect</a>
          </div>
        </div>
      </form>
    </div>

    <div class="error-box has-text-danger is-size-5" v-if="showError">
      <p><div class="fa fa-times"></div> {{ errorMsg }}</p>
    </div>

    <hr v-if="output !== null">

    <div id='analyzer-output' v-if="output !== null">
      <label class='label'>Output</label>
      <div class="output-control">
        <a class="has-text-primary" v-if="!rawView" v-on:click="setRawView(true)">Show Feature-Value Pairs</a>
        <a class="has-text-primary" v-if="rawView" v-on:click="setRawView(false)">Show Simplified Analyses</a>
      </div>
      <div class='box analysis-wrapper'>
        <analyzer-output v-bind:lang='lang' v-bind:outputData='output' v-bind:rawView="rawView"></analyzer-output>
      </div>
    </div>

    <div class="bibliography">
      <p>[1]
        <a href="/static/CalimaStar.pdf" target="_blank">
          Dima Taji, Salam Khalifa, Ossama Obeid, Fadhl Eryani and Nizar Habash.
          <strong>An Arabic Morphological Analyzer and Generator with Copious Features.</strong>
          <i>Workshop on Computational Research in Phonetics, Phonology, and Morphology.
          The Confernece on Empirical Methods in Natural Language Processing (EMNLP 2018), Brussels, Belgium.</i>
        </a>
      </p>
    </div>
  </div>
</template>

<script>
import lodash from 'lodash';
import AnalyzerOutput from './Shared/AnalyzerOutput';
import { feat4pos } from './Shared/genFeats.json';
import { translations } from './Shared/translations.json';

export default {
  name: 'Reinflector',
  components: {
    AnalyzerOutput,
  },
  data() {
    const feats = {};
    // const featList = Object.keys(featVals);
    const featList = ['prc3', 'prc2', 'prc1', 'prc0', 'enc0', 'per', 'gen',
      'num', 'cas', 'stt', 'vox', 'mod', 'asp'];
    const posListSorted = lodash.sortBy(Object.keys(feat4pos));
    const pos = posListSorted[1];

    featList.sort();

    for (let i = 0; i < featList.length; i += 1) {
      const feat = featList[i];
      const featProps = {
        selected: false,
      };

      if (feat4pos[pos][feat]) {
        const featVal = feat4pos[pos][feat].required ? feat4pos[pos][feat].options[0] : '*';
        featProps.value = featVal;
      }

      feats[feat] = featProps;
    }

    return {
      lemma: '',
      word: '',
      pos,
      posList: posListSorted,
      feats,
      lang: 'en',
      errorMsg: 'An error occurred during reinflection.',
      showError: false,
      translations,
      featList,
      feat4pos,
      output: null,
      selectedFeat: null,
      rawView: false,
      cliticFeats: ['prc3', 'prc2', 'prc1', 'prc0', 'enc0'],
      otherFeats1: ['per', 'gen', 'num', 'cas'],
      otherFeats2: ['stt', 'vox', 'mod', 'asp'],
      lodash,
    };
  },

  watch: {
    pos(newPOS, oldPOS) {
      for (let i = 0; i < this.featList.length; i += 1) {
        const feat = this.featList[i];
        if (feat4pos[newPOS][feat]) {
          const tmpFeatVal = this.cliticFeats.includes(feat) ? '0' : '*';
          const featVal = feat4pos[oldPOS][feat] &&
            feat4pos[newPOS][feat].options.includes(this.feats[feat].value) ?
            this.feats[feat].value : tmpFeatVal;
          this.feats[feat].value = featVal;
        }
      }
    },
  },

  methods: {
    reinflect() {
      this.output = null;
      this.showError = false;

      const word = this.word.trim();

      if (word.length === 0 || word.split(/\s/).length !== 1) {
        // TODO: Throw an error
        return;
      }

      const featBuffer = [
        `word=${encodeURIComponent(this.word)}`,
      ];

      const lemma = this.lemma.trim();
      if (lemma.length !== 0) {
        featBuffer.push(`lex=${encodeURIComponent(lemma)}`);
      }

      if (this.pos !== '*') {
        featBuffer.push(`pos=${encodeURIComponent(this.pos)}`);
      }

      for (let i = 0; i < this.featList.length; i += 1) {
        const feat = this.featList[i];
        if (this.feat4pos[this.pos] &&
            this.feat4pos[this.pos][feat] &&
            this.feats[feat].value !== '*' &&
            this.feats[feat].value !== '0') {
          const featVal = this.feats[feat].value;
          featBuffer.push(`${encodeURIComponent(feat)}=${encodeURIComponent(featVal)}`);
        }
      }

      this.$http.get(`${API_URL}/reinflect/?${featBuffer.join('&')}`)
        .then((response) => {
          this.output = response.body;
        })
        .catch((error) => {
          this.showError = true;
          // eslint-disable-next-line
          console.log(error);
        });
    },
    setRawView(v) {
      this.rawView = v;
    },
  },

  mounted() {},
};
</script>

<style lang='scss' scoped>

.container {
  padding-left: 1rem;
  padding-right: 1rem;
}

label.checkbox {
  margin-right: 1rem;
}

.analysis-wrapper {
  width: 100%;
  height: 30rem;
  overflow: scroll;
}

.ar-input {
  font-size: 1em;
}

.align-bottom {
  margin-top: auto;
  margin-bottom: 0;
}
</style>



// WEBPACK FOOTER //
// src/components/Reinflector.vue